import { useState } from "react"
import { UserPlus, Database, Upload, Download } from "lucide-react"
import { Button } from "../components/ui/button"
import { SchoolSidebar } from "../components/ui/school/school-dashboard/school-sidebar"
import { StudentStats } from "../components/ui/school/data-center/student-stats"
import { StudentTable } from "../components/ui/school/data-center/student-table"
import { AddStudentModal } from "../components/ui/school/data-center/add-student-modal"
import { EditStudentModal } from "../components/ui/school/data-center/edit-student-modal"
import { DeleteConfirmModal } from "../components/ui/school/data-center/delete-confirm-modal"
import { DownloadFormatModal } from "../components/ui/school/data-center/download-format-modal"
import { initialStudents } from "../lib/students-data"
import { UploadStudentsDataModal } from "../components/ui/school/data-center/upload-students-data-modal"

const DataCenterPage = () => {
  const [isSidebarOpen, setIsSidebarOpen] = useState(true)
  const [students, setStudents] = useState(initialStudents)
  const [isAddModalOpen, setIsAddModalOpen] = useState(false)
  const [isUploadModalOpen, setIsUploadModalOpen] = useState(false)
  const [isDownloadFormatModalOpen, setIsDownloadFormatModalOpen] = useState(false)
  const [isEditModalOpen, setIsEditModalOpen] = useState(false)
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false)
  const [selectedStudent, setSelectedStudent] = useState(null)
  const [deleteStudentId, setDeleteStudentId] = useState("")
  const [currentPage, setCurrentPage] = useState(1)

  // Calculate stats
  const totalStudents = students.length
  const avgPerformance = (students.reduce((sum, s) => sum + Number.parseFloat(s.gpa), 0) / totalStudents).toFixed(1)

  // Find most popular career
  const careerCounts = students.reduce((acc, s) => {
    acc[s.careerPath] = (acc[s.careerPath] || 0) + 1
    return acc
  }, {})
  const topCareer = Object.entries(careerCounts).sort((a, b) => b[1] - a[1])[0]?.[0] || "N/A"

  // Pagination
  const studentsPerPage = 10
  const totalPages = Math.ceil(totalStudents / studentsPerPage)
  const startIndex = (currentPage - 1) * studentsPerPage
  const endIndex = startIndex + studentsPerPage
  const currentStudents = students.slice(startIndex, endIndex)

  const handleAddStudent = (newStudent) => {
    const colors = ["orange", "purple", "pink", "indigo", "teal", "blue", "green", "amber", "red", "cyan"]
    const initials = newStudent.name
      .split(" ")
      .map((n) => n[0])
      .join("")
      .toUpperCase()
      .slice(0, 2)

    const student = {
      ...newStudent,
      id: (students.length + 1).toString(),
      studentId: `KB-2023-${(students.length + 1).toString().padStart(3, "0")}`,
      initials,
      initialsColor: colors[students.length % colors.length],
    }
    setStudents([...students, student])
  }

  const handleUploadFile = (file, uploadType, classId) => {
    console.log("Uploaded file:", file.name, file.type, file.size)
    console.log("Upload type:", uploadType, "Class:", classId)
    // TODO: Process the uploaded file and update students database
    const message =
      uploadType === "class"
        ? `File "${file.name}" uploaded successfully for ${classId}! Processing ${file.type} file...`
        : `File "${file.name}" uploaded successfully for whole school! Processing ${file.type} file...`
    alert(message)
  }

  const handleEditStudent = (student) => {
    setSelectedStudent(student)
    setIsEditModalOpen(true)
  }

  const handleUpdateStudent = (updatedStudent) => {
    setStudents(students.map((s) => (s.id === updatedStudent.id ? updatedStudent : s)))
  }

  const handleDeleteClick = (id) => {
    setDeleteStudentId(id)
    setIsDeleteModalOpen(true)
  }

  const handleConfirmDelete = () => {
    setStudents(students.filter((s) => s.id !== deleteStudentId))
    setIsDeleteModalOpen(false)
    setDeleteStudentId("")
  }

  const studentToDelete = students.find((s) => s.id === deleteStudentId)

  return (
    <div className="flex min-h-screen bg-gray-50">
      <SchoolSidebar isOpen={isSidebarOpen} onToggle={() => setIsSidebarOpen(!isSidebarOpen)} />

      {/* Mobile Overlay */}
      {isSidebarOpen && (
        <div className="md:hidden fixed inset-0 bg-black/50 z-30" onClick={() => setIsSidebarOpen(false)} />
      )}

      <main className={`flex-1 transition-all duration-300 ${isSidebarOpen ? "md:ml-64" : ""}`}>

        {/* Main Content */}
        <div className="max-w-7xl w-full mx-auto px-4 sm:px-6 lg:px-8 py-10">
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-6 mb-10">
            <div>
              <h1 className="text-3xl font-bold text-secondary mb-2">Data Center</h1>
              <p className="text-muted-foreground">
                Manage student records, academic performance, and guardian details.
              </p>
            </div>
            <div className="flex flex-col sm:flex-row gap-3">
              <Button
                onClick={() => setIsDownloadFormatModalOpen(true)}
                variant="outline"
                className="border-teal-600 text-teal-600 hover:bg-teal-50"
              >
                <Download className="w-5 h-5 mr-2" />
                Download Format
              </Button>
              <Button
                onClick={() => setIsUploadModalOpen(true)}
                variant="outline"
                className="border-primary text-primary hover:bg-primary/10"
              >
                <Upload className="w-5 h-5 mr-2" />
                Upload Students Data
              </Button>
              <Button
                onClick={() => setIsAddModalOpen(true)}
                className="bg-primary hover:bg-primary/90 text-white shadow-lg hover:shadow-xl transition-all"
              >
                <UserPlus className="w-5 h-5 mr-2" />
                Add Student
              </Button>
            </div>
          </div>

          <StudentStats totalStudents={totalStudents} avgPerformance={avgPerformance} topCareer={topCareer} />

          <StudentTable
            students={currentStudents}
            onEdit={handleEditStudent}
            onDelete={handleDeleteClick}
            currentPage={currentPage}
            totalPages={totalPages}
            onPageChange={setCurrentPage}
          />
        </div>
      </main>

      {/* Modals */}
      <AddStudentModal isOpen={isAddModalOpen} onClose={() => setIsAddModalOpen(false)} onAdd={handleAddStudent} />

      <UploadStudentsDataModal
        isOpen={isUploadModalOpen}
        onClose={() => setIsUploadModalOpen(false)}
        onUpload={handleUploadFile}
      />

      <DownloadFormatModal isOpen={isDownloadFormatModalOpen} onClose={() => setIsDownloadFormatModalOpen(false)} />

      <EditStudentModal
        isOpen={isEditModalOpen}
        onClose={() => {
          setIsEditModalOpen(false)
          setSelectedStudent(null)
        }}
        onUpdate={handleUpdateStudent}
        student={selectedStudent}
      />

      <DeleteConfirmModal
        isOpen={isDeleteModalOpen}
        onClose={() => {
          setIsDeleteModalOpen(false)
          setDeleteStudentId("")
        }}
        onConfirm={handleConfirmDelete}
        studentName={studentToDelete?.name || ""}
      />
    </div>
  )
}

export default DataCenterPage